// Wire schema for the world WebSocket. Newline-free JSON, one envelope per
// WebSocket message. We tag with a string `t` discriminant rather than a numeric
// opcode — the message volume here (movement excluded) is tiny and the human
// readability is worth more than the byte savings.
//
// `Move` is the one hot-path message; we keep it small (3 numeric fields) and
// rate-limit on the server.
//
// All coordinates are integer pixels in world space (not tile coordinates).
//
// Duel messages ride this same socket (muxed). The heavy lifting — decoding the
// OCGcore message stream, masking hidden info, and building board snapshots —
// happens in the engine; here we only carry the already-masked results
// (DuelBoard / DuelEvent from @ygoevo/protocol) and the player's semantic
// choices (DuelChoice), which apps/world encodes into raw OCG responses.
/** The card-sleeve everyone owns from the start (the classic sleeveless card back); collectibles
 *  are granted on top. */
export const DEFAULT_SLEEVE_ID = "default";
export const RARITIES = ["N", "R", "SR", "UR"];
/** Shards to craft one copy, by rarity (exponential). */
export const CRAFT_COST = { N: 20, R: 60, SR: 250, UR: 1000 };
/** Shards gained per copy dismantled, by rarity — always far below the craft cost. Commons/rares
 *  yield almost nothing on purpose: junk can't be farmed into high-tier material (you need to
 *  dismantle high-tier dupes), so the Shards a player holds genuinely reflects rarity/value. */
export const DISMANTLE_YIELD = { N: 1, R: 5, SR: 60, UR: 200 };
/** Human label for a rarity tier. */
export const RARITY_LABEL = {
    N: "Common",
    R: "Rare",
    SR: "Super Rare",
    UR: "Ultra Rare",
};
export const FOIL_GRADES = [1, 2, 3];
export const FOIL_INFO = {
    1: { name: "holo", label: "Holo Foil", weight: 70, color: "#7fe9ff" },
    2: { name: "prism", label: "Prismatic Foil", weight: 26, color: "#c79bff" },
    3: { name: "galaxy", label: "Galaxy Foil", weight: 4, color: "#ffd36e" },
};
/** Chance any single pulled card comes as a foil (independent of card rarity). */
export const FOIL_PULL_CHANCE = 0.07;
/** Extra shards a foil yields on dismantle, ON TOP of the card's plain rarity yield. */
export const FOIL_DISMANTLE_BONUS = { 1: 30, 2: 120, 3: 400 };
/** Total shards from dismantling one foil copy (plain rarity yield + grade bonus). */
export function foilDismantleYield(r, grade) {
    return DISMANTLE_YIELD[r] + (FOIL_DISMANTLE_BONUS[grade] ?? 0);
}
/** Every collectible sleeve's rarity (id → rarity). Mirrors the visual catalog in
 *  clients/web/src/lib/duel/sleeves.ts — the source of truth for the Sleeve Box pool + tiering.
 *  The free default (DEFAULT_SLEEVE_ID) is intentionally absent. */
export const SLEEVE_RARITY = {
    "df-cyber": "rare",
    crimson: "rare",
    abyss: "rare",
    verdant: "rare",
    aureate: "epic",
    obsidian: "epic",
    prism: "legendary",
};
/** Build a SKU id from silhouette + colour, e.g. ("dd_03", 2) → "dd_03_c02". */
export const diskSku = (silhouetteId, color) => `${silhouetteId}_c${String(color).padStart(2, "0")}`;
const dc = (silhouetteId, color, opts = {}) => ({ color, sku: diskSku(silhouetteId, color), ...opts });
/** The six disk silhouettes with all their colourways. Order = shop display order.
 *  Gold colourways (one per silhouette, identified by dominant-hue scan of the
 *  original 2008 art) are exclusive — only `BETA_GOLD_DUELDISK_SKU` is granted today
 *  (to beta players); the rest are reserved for future content. */
export const DUEL_DISKS = [
    { id: "dd_01", slot: "dd", variant: "01", name: "Standard Disk", rarity: "common", shop: "kame",
        colors: [dc("dd_01", 1, { name: "Silver" }), dc("dd_01", 2, { name: "Onyx" }), dc("dd_01", 3, { name: "Crimson" }), dc("dd_01", 4, { name: "Sapphire" }), dc("dd_01", 5, { name: "Gold", rarity: "legendary", exclusive: true })] },
    { id: "dd_02", slot: "dd", variant: "02", name: "Skywing Disk", rarity: "legendary", shop: "kame", // ultra: sleek flat blue-winged disk
        colors: [dc("dd_02", 1, { name: "Steel" }), dc("dd_02", 2, { name: "Gold", rarity: "legendary", exclusive: true }), dc("dd_02", 3, { name: "Slate" }), dc("dd_02", 4, { name: "Onyx" })] },
    { id: "da_02", slot: "da", variant: "02", name: "Vortex Disk", rarity: "rare", shop: "academy",
        colors: [dc("da_02", 1, { name: "Steel" }), dc("da_02", 2, { name: "Gold", rarity: "legendary", exclusive: true }), dc("da_02", 3, { name: "Slate" }), dc("da_02", 4, { name: "Onyx" })] },
    { id: "dd_03", slot: "dd", variant: "03", name: "Battle Disk", rarity: "common", shop: "kame",
        colors: [dc("dd_03", 1, { name: "Steel" }), dc("dd_03", 2, { name: "Gold", rarity: "legendary", exclusive: true }), dc("dd_03", 3, { name: "Slate" }), dc("dd_03", 4, { name: "Onyx" })] },
    { id: "da_03", slot: "da", variant: "03", name: "Phantom Disk", rarity: "epic", shop: "academy",
        colors: [dc("da_03", 1, { name: "Teal" }), dc("da_03", 2, { name: "Gold", rarity: "legendary", exclusive: true }), dc("da_03", 3, { name: "Cobalt" }), dc("da_03", 4, { name: "Onyx" })] },
];
/** The free SKU every duelist owns from the start (Standard Disk in Silver). */
export const DEFAULT_DUELDISK_SKU = "dd_01_c01";
/** The single beta-reward SKU — the gold colourway of the Battle Disk. The other 5 gold
 *  colourways are exclusive too but not yet grantable (reserved for future content). */
export const BETA_GOLD_DUELDISK_SKU = "dd_03_c02";
// ─── Daily login rewards ───────────────────────────────────────────────────────
// The daily streak pays DP on most days, with the Gold Duel Disk slotted on day 2 (a one-time
// grant — see rewards.ts; if you already own it you get DP instead). Shared so the client can show
// the upcoming-rewards preview without a separate server round-trip.
/** Streak day whose reward is the Gold Duel Disk instead of DP. */
export const DAILY_GOLD_DAY = 2;
/** DP a given streak day pays: 200 on day 1, +100/day, capped at 1000 (day 9+). */
export const dailyDp = (day) => Math.min(200 + (Math.max(1, day) - 1) * 100, 1000);
export const dailyRewardKind = (day) => (day === DAILY_GOLD_DAY ? "golddisk" : "dp");
const DUEL_DISK_BY_ID = Object.fromEntries(DUEL_DISKS.map((d) => [d.id, d]));
const DUEL_DISK_BY_SKU = Object.fromEntries(DUEL_DISKS.flatMap((def) => def.colors.map((c) => [c.sku, { def, c }])));
/** Look up a disk colourway by its SKU. */
export function findDuelDisk(sku) {
    return DUEL_DISK_BY_SKU[sku] ?? null;
}
/** Resolve the SKU for a worn (slot, variant, color), or null if not a known colourway. */
export function duelDiskSkuFor(slot, variant, color) {
    const sku = diskSku(`${slot}_${variant}`, color);
    return DUEL_DISK_BY_SKU[sku] ? sku : null;
}
/** Effective rarity of a disk colourway: its own override (e.g. gold → legendary) or
 *  the silhouette's tier when unset. */
export function duelDiskColorRarity(def, c) {
    return c.rarity ?? def.rarity;
}
export const GACHA_RARITIES = ["common", "rare", "super", "ultra"];
/** Build an outfit SKU from slot + variant + colour, e.g. ("dc", "02", 3) → "dc_02_c03". */
export const outfitSku = (slot, variant, color) => `${slot}_${variant}_c${String(color).padStart(2, "0")}`;
const oc = (slot, variant, color) => ({ color, sku: outfitSku(slot, variant, color) });
/** The dc "Outfit" + ac "Costume" looks with their colourways (colours = the union across
 *  genders; the manifest art decides which actually render for a given body). Order = shop
 *  display order. Mirrors the `dc`/`ac` variants in packages/yo2-data avatars-portrait.json.
 *  ac uniform variants are listed so a worn one resolves to a SKU, but outfitPrice() never
 *  sells a variant that's a rank uniform for the buyer's gender. */
export const OUTFITS = [
    { id: "dc_01", slot: "dc", variant: "01", name: "Starter Outfit", rarity: "common", colors: [oc("dc", "01", 1), oc("dc", "01", 2), oc("dc", "01", 3), oc("dc", "01", 4)] },
    { id: "dc_02", slot: "dc", variant: "02", name: "Street Duelist Outfit", rarity: "common", colors: [oc("dc", "02", 1), oc("dc", "02", 2), oc("dc", "02", 3)] },
    { id: "dc_03", slot: "dc", variant: "03", name: "Black Suit", rarity: "common", colors: [oc("dc", "03", 1)] },
    { id: "dc_04", slot: "dc", variant: "04", name: "Fitted Tank & Jeans", rarity: "rare", colors: [oc("dc", "04", 1)] }, // sleeveless buckled top + slim jeans (not actually Yugi's)
    { id: "dc_05", slot: "dc", variant: "05", name: "Society of Light Suit", rarity: "super", collectible: true, colors: [oc("dc", "05", 1)] }, // Sartorius / Society of Light — story collectible, not rolled/sold
    { id: "dc_06", slot: "dc", variant: "06", name: "Casual Duelist Fit", rarity: "common", colors: [oc("dc", "06", 1)] },
    { id: "dc_98", slot: "dc", variant: "98", name: "Rare Hunter Cloak", rarity: "ultra", colors: [oc("dc", "98", 1)] }, // Rare Hunter / Seeker; ships with the h1_98 hood
    { id: "dc_99", slot: "dc", variant: "99", name: "Gilded Oracle Robes", rarity: "super", colors: [oc("dc", "99", 1)] }, // dark hooded robe, gold sun medallion + bangles
    // ac "Costume" looks (single colour each). Variants 02/03/05/06/07/08 are rank uniforms for
    // one gender or the other (see SCHOOL_UNIFORM) — kept here so they resolve, never sold/rolled.
    { id: "ac_01", slot: "ac", variant: "01", name: "Crimson Duelist Jacket", rarity: "common", collectible: true, colors: [oc("ac", "01", 1)] }, // red Slifer-lookalike — kept out of drops so it isn't confused with the earned Slifer uniform (ac_08)
    { id: "ac_02", slot: "ac", variant: "02", name: "Academy Costume II", rarity: "common", colors: [oc("ac", "02", 1)] },
    { id: "ac_03", slot: "ac", variant: "03", name: "Academy Costume III", rarity: "common", colors: [oc("ac", "03", 1)] },
    { id: "ac_04", slot: "ac", variant: "04", name: "Duel Academy Tracksuit", rarity: "common", collectible: true, colors: [oc("ac", "04", 1)] }, // collectible — earned, not sold/rolled
    { id: "ac_05", slot: "ac", variant: "05", name: "Academy Costume V", rarity: "common", colors: [oc("ac", "05", 1)] },
    { id: "ac_06", slot: "ac", variant: "06", name: "Academy Costume VI", rarity: "common", colors: [oc("ac", "06", 1)] },
    { id: "ac_07", slot: "ac", variant: "07", name: "Academy Costume VII", rarity: "common", colors: [oc("ac", "07", 1)] },
    { id: "ac_08", slot: "ac", variant: "08", name: "Slifer Red Jacket (Jaden Yuki)", rarity: "common", colors: [oc("ac", "08", 1)] }, // Slifer dorm uniform (cm) — earn-only via the ranked ladder, never dropped/sold
    { id: "ac_09", slot: "ac", variant: "09", name: "Chazz Princeton Uniform", rarity: "super", colors: [oc("ac", "09", 1)] }, // Chazz
    { id: "ac_10", slot: "ac", variant: "10", name: "Azure Duelist Coat", rarity: "common", collectible: true, colors: [oc("ac", "10", 1)] }, // Academy variant — story collectible, not sold/rolled
    { id: "ac_11", slot: "ac", variant: "11", name: "Hell Kaiser Zane Outfit", rarity: "ultra", colors: [oc("ac", "11", 1)] }, // Zane (Cyberdark era)
    { id: "ac_12", slot: "ac", variant: "12", name: "Aster Phoenix Suit", rarity: "super", colors: [oc("ac", "12", 1)] }, // Aster
];
/** The free starter outfit silhouette — every colour of it is granted, the rest are bought. */
export const DEFAULT_OUTFIT_ID = "dc_01";
/** SKUs every duelist owns from the start: every colourway of the default outfit. */
export const DEFAULT_OUTFIT_SKUS = OUTFITS.find((o) => o.id === DEFAULT_OUTFIT_ID).colors.map((c) => c.sku);
const OUTFIT_BY_SKU = Object.fromEntries(OUTFITS.flatMap((def) => def.colors.map((c) => [c.sku, { def, c }])));
/** Look up an outfit/costume colourway by its SKU. */
export function findOutfit(sku) {
    return OUTFIT_BY_SKU[sku] ?? null;
}
/** Resolve the SKU for a worn (slot, variant, color), or null if not a known outfit colourway. */
export function outfitSkuFor(slot, variant, color) {
    const sku = outfitSku(slot, variant, color);
    return OUTFIT_BY_SKU[sku] ? sku : null;
}
/** Outfit/costume silhouettes whose art is male-only (no `cw` cels). A female body can't wear or
 *  roll these, so they're filtered from a `cw` player's Outfit Box. Mirrors the gendered `dc`/`ac`
 *  variant coverage in avatars-portrait.json. */
const CM_ONLY_OUTFITS = new Set(["dc_05", "dc_06", "ac_08", "ac_09", "ac_10", "ac_11", "ac_12"]);
/** Does this body have art for the outfit silhouette? */
export function outfitForGender(id, gender) {
    return gender === "cm" || !CM_ONLY_OUTFITS.has(id);
}
// ── Hooded cloaks ──────────────────────────────────────────────────────────────
// Some cloaks ship as a SET with a matching front-hair "hood" (an `h1` variant). The hood is locked
// until you own the cloak (gated by cloak ownership — no separate grant), it's shown up in the
// cloak's box/wardrobe preview, and equipping the cloak auto-equips it. All OTHER hairs are free.
/** Cloak outfit id → the `h1` hood variant it ships with. */
export const OUTFIT_HOOD = { dc_98: "98", dc_99: "99" };
/** The `h1` hood variant a cloak ships with, or null. */
export const hoodForOutfit = (outfitId) => OUTFIT_HOOD[outfitId] ?? null;
/** Which cloak outfit id a given `h1` variant is the hood of (reverse), or null if it's a normal hair. */
export function cloakForHood(hairVariant) {
    for (const [outfitId, v] of Object.entries(OUTFIT_HOOD))
        if (v === hairVariant)
            return outfitId;
    return null;
}
/** Standalone "collectible" front-hairs (`h1`) that are NOT free and have no unlock path yet — locked
 *  until a future story/grant system hands them out (like the academy items). Every other hair (apart
 *  from the cloak hoods above) is free for everyone. */
export const COLLECTIBLE_HAIRS = new Set(["14"]); // h1_14 — the Kuriboh/horned hair
/** Is this `h1` front-hair locked (a cloak hood you don't own, or a collectible)? Hoods unlock via
 *  cloak ownership — pass `ownsCloak` to resolve those; collectibles are always locked for now. */
export function hairLocked(variant, ownsCloak) {
    const cloak = cloakForHood(variant);
    if (cloak)
        return !ownsCloak(cloak);
    return COLLECTIBLE_HAIRS.has(variant);
}
export const BOX_IDS = ["outfit", "dueldisk", "sleeve"];
export const BOXES = [
    { id: "outfit", name: "Outfit Box", kind: "outfit", price: 600, blurb: "A random outfit." },
    { id: "dueldisk", name: "Duel Disk Box", kind: "dueldisk", price: 600, blurb: "A random duel-disk colourway." },
    { id: "sleeve", name: "Sleeve Box", kind: "sleeve", price: 600, blurb: "Fresh card sleeves, in the works.", comingSoon: true },
];
export const boxDef = (id) => BOXES.find((b) => b.id === id);
/** Flat pull odds (percent). Every pull uses these; the pity is SHARDS (below), not odds-bending. */
export const GACHA_BASE_WEIGHTS = { common: 79, rare: 15, super: 5, ultra: 1 };
// Pity = the SAME crafting Shards as cards (no separate "dust" currency). A box duplicate is never a
// dead pull: it drops a tradeable copy AND dismantles into Shards (the shared DISMANTLE_YIELD table,
// mapped by rarity). Bank PICK_COST[rarity] Shards and PICK any unowned item of that rarity — a hard
// guarantee, so nothing is ever a luck-wall. Because common/rare yields are tiny, you can't farm
// junk into high-tier items; and because cosmetics are ~50× harder to pull than cards, the pick
// costs run 2.5× the equivalent card craft — a real, trade-anchoring premium that's still reachable.
/** Cosmetic rarity ↔ card rarity (the ladders are isomorphic; cosmetics share the Shards economy). */
export const GACHA_TO_RARITY = { common: "N", rare: "R", super: "SR", ultra: "UR" };
/** Shards a box duplicate of this rarity dismantles into (the shared card DISMANTLE_YIELD). */
export const cosmeticShardYield = (r) => DISMANTLE_YIELD[GACHA_TO_RARITY[r]];
/** Shards to PICK any unowned item of this rarity from a box's pool. Same 1:3:12:50 rarity curve as
 *  card crafting (CRAFT_COST), scaled 2.5× because cosmetics are ~50× harder to pull than cards — a
 *  prestige/trade-anchoring premium, capped well short of the true 50× so it stays attainable. */
export const PICK_COST = { common: 50, rare: 150, super: 600, ultra: 2500 };
/** Map a disk colourway's tier onto the gacha ladder. */
export function diskGachaRarity(r) {
    return r === "legendary" ? "ultra" : r === "epic" ? "super" : r; // common/rare unchanged
}
/** Map a sleeve's tier onto the gacha ladder. */
export function sleeveGachaRarity(r) {
    return r === "legendary" ? "ultra" : r === "epic" ? "super" : r === "rare" ? "rare" : "common";
}
/** Gacha rarity of any cosmetic SKU (server + client share this). */
export function gachaRarityOf(kind, sku) {
    if (kind === "outfit")
        return findOutfit(sku)?.def.rarity ?? "common";
    if (kind === "dueldisk") {
        const e = findDuelDisk(sku);
        return e ? diskGachaRarity(duelDiskColorRarity(e.def, e.c)) : "common";
    }
    return sleeveGachaRarity(SLEEVE_RARITY[sku] ?? "rare");
}
/** Every SKU a box can roll, for a given body (gender only narrows the Outfit Box). Excludes the
 *  free default outfit, grant-only collectibles, rank uniforms, and grant-only gold disks. */
export function boxPool(id, gender) {
    if (id === "outfit") {
        const out = [];
        for (const o of OUTFITS) {
            if (o.id === DEFAULT_OUTFIT_ID || o.collectible || !outfitForGender(o.id, gender))
                continue;
            // Academy DORM UNIFORMS (Slifer/Ra/Obelisk-style — a rank-uniform variant in either gender) are
            // rank rewards, never rolled. Distinct character costumes (Aster, Chazz, Hell Kaiser, …) stay sellable.
            if (o.slot === "ac" && (uniformSchoolsForVariant("cm", o.variant).length > 0 || uniformSchoolsForVariant("cw", o.variant).length > 0))
                continue;
            for (const c of o.colors)
                out.push({ kind: "outfit", sku: c.sku, rarity: o.rarity });
        }
        return out;
    }
    if (id === "dueldisk") {
        const out = [];
        for (const d of DUEL_DISKS)
            for (const c of d.colors) {
                if (c.exclusive)
                    continue; // gold colourways are grant-only
                out.push({ kind: "dueldisk", sku: c.sku, rarity: diskGachaRarity(duelDiskColorRarity(d, c)) });
            }
        return out;
    }
    // sleeve box: every purchasable sleeve (the free default is absent from SLEEVE_RARITY)
    return Object.entries(SLEEVE_RARITY).map(([sku, r]) => ({ kind: "sleeve", sku, rarity: sleeveGachaRarity(r) }));
}
/** Roll one rarity tier from a weight table, given an rng() in [0,1). */
export function rollRarity(w, rng) {
    const total = GACHA_RARITIES.reduce((s, t) => s + w[t], 0);
    let x = rng() * total;
    for (const t of GACHA_RARITIES) {
        x -= w[t];
        if (x < 0)
            return t;
    }
    return "common";
}
/** Roll one item from a box: pick a rarity by the flat weights, bumping to the nearest tier the pool
 *  actually has — DOWN first, then up — so an empty MIDDLE tier (e.g. a pool with only common+ultra)
 *  defaults to the lower tier rather than inflating the rarest one; a pool with no commons (sleeves)
 *  still floors upward. Then a uniform item of that tier. Pure given rng() in [0,1). */
export function rollBox(pool, rng) {
    let tier = rollRarity(GACHA_BASE_WEIGHTS, rng);
    if (!pool.some((e) => e.rarity === tier)) {
        const i = GACHA_RARITIES.indexOf(tier);
        const order = [...GACHA_RARITIES.slice(0, i).reverse(), ...GACHA_RARITIES.slice(i + 1)];
        tier = order.find((t) => pool.some((e) => e.rarity === t)) ?? pool[0].rarity;
    }
    const items = pool.filter((e) => e.rarity === tier);
    return items[Math.floor(rng() * items.length)];
}
export const DUEL_FORMATS = ["world", "free"];
export const SIDE_DECK_SECONDS = 90; // how long each player has to side / pick before the next game
export const LADDERS = ["world", "free-single", "free-match"];
/** The ladder a rated game belongs to (world keeps one unified ladder regardless of length). */
export function ladderOf(format, bestOf) {
    if (format === "world")
        return "world";
    return bestOf === 3 ? "free-match" : "free-single";
}
/** Rated games a player must finish before leaving the Beginner (placement) state. */
export const PLACEMENT_GAMES = 5;
/** Placed-player tiers, ascending by displayed rating. The first entry is the floor —
 *  no placed player drops below Slifer Red I. Thresholds are tunable. */
export const RANK_LADDER = [
    { min: -Infinity, school: "slifer", division: 1, label: "Slifer Red I" },
    { min: 1400, school: "slifer", division: 2, label: "Slifer Red II" },
    { min: 1550, school: "ra", division: 1, label: "Ra Yellow I" },
    { min: 1700, school: "ra", division: 2, label: "Ra Yellow II" },
    { min: 1850, school: "obelisk", division: 1, label: "Obelisk Blue I" },
    { min: 2000, school: "obelisk", division: 2, label: "Obelisk Blue II" },
    { min: 2150, school: "master", division: 0, label: "Master" },
];
/** School progression order (also the uniform-unlock order). */
export const RANK_SCHOOLS = ["beginner", "slifer", "ra", "obelisk", "master"];
/** Map a displayed rating to a tier. Unplaced → Beginner. */
export function tierOf(rating, placed) {
    if (!placed)
        return { min: -Infinity, school: "beginner", division: 0, label: "Beginner" };
    let step = RANK_LADDER[0];
    for (const s of RANK_LADDER)
        if (rating >= s.min)
            step = s;
    return step;
}
/** A monotonic ordinal for a tier (school×10 + division) so promotions/demotions compare cleanly. */
export function tierOrdinal(school, division) {
    return RANK_SCHOOLS.indexOf(school) * 10 + division;
}
/** School uniforms earned at the given school (cumulative, lowest → current). */
export function unlockedUniforms(school) {
    const idx = RANK_SCHOOLS.indexOf(school);
    return ["slifer", "ra", "obelisk", "master"].filter((s) => RANK_SCHOOLS.indexOf(s) <= idx);
}
/** GX dorm uniform avatar variant (the `ac` overlay slot) per school + gender. Editable:
 *  swap variant ids to re-skin a tier. Master reuses Obelisk's female art until a dedicated
 *  female elite uniform is wired. */
export const SCHOOL_UNIFORM = {
    slifer: { cm: "08", cw: "05" },
    ra: { cm: "02", cw: "06" },
    obelisk: { cm: "03", cw: "07" },
    master: { cm: "06", cw: "07" },
};
/** Which school(s) own a given `ac` uniform variant for the gender — empty means it's a free
 *  accessory, not a gated uniform. Female Obelisk + Master currently share art, so a variant can
 *  map to more than one school; owning ANY of them unlocks it. */
export function uniformSchoolsForVariant(gender, variant) {
    return Object.keys(SCHOOL_UNIFORM).filter((s) => SCHOOL_UNIFORM[s][gender] === variant);
}
// ─── Helpers ────────────────────────────────────────────────────────────────
// Runtime validation for inbound messages (the untrusted wire). The schema is compile-time
// locked to the ClientMessage type above — see schema.ts.
export { clientMessageSchema, parseClientMessage } from "./schema.js";
export const isClientMessage = (m) => typeof m === "object" && m !== null && "t" in m;
export const encode = (m) => JSON.stringify(m);
export function decode(s) {
    return JSON.parse(s);
}
//# sourceMappingURL=index.js.map